/* Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { Component, EventEmitter, Inject, Optional, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NV_HIG_CONFIG, NvHigConfig } from '../../hig.config';
import { TvImageSizeScale } from '../../models/hig-constants';
import {
    NvHigListItemLeadingElementType,
    NvHigListItemData,
    NvHigListItemTrailingElementType
} from '../../models/hig-list-item.types';
import { NvListItem } from '../../models/hig-modal-shared.types';
import { NvHigListInsertComponent } from '../nv-hig-list-insert/nv-hig-list-insert.component';
import { NvHigListItemCoreComponent } from './nv-hig-list-item-core/nv-hig-list-item-core.component';
import { NvHigListItemLeadComponent } from './nv-hig-list-item-lead/nv-hig-list-item-lead.component';
import { NvHigListItemTrailComponent } from './nv-hig-list-item-trail/nv-hig-list-item-trail.component';

/**
 * Place the list-item directly inside a container grid which applies a 3 column grid layout.
    Do not define the number of rows explicitly, the list-item can implicitly take
    up to 2 CSS grid rows depending on the content. E.g. CSS:
        `.grid {
            display: grid;
            grid-template-columns: auto 1fr auto;
        }`
 */
@Component({
    selector: 'nv-hig-list-item',
    templateUrl: './nv-hig-list-item.component.html',
    styleUrl: './nv-hig-list-item.component.scss',
    imports: [
        NvHigListItemLeadComponent,
        NvHigListItemCoreComponent,
        NvHigListItemTrailComponent,
        NvHigListInsertComponent
    ]
})
export class NvHigListItemComponent {
    data: NvHigListItemData;
    @Input() set cardData(x: NvHigListItemData) {
        this.data = x;
        // Set default aria-label for trailing element if not provided
        if (this.data.trail && !this.data.trail?.ariaLabel) {
            this.data.trail.ariaLabel = this.getTranslatedAriaLabelForTrail(
                this.data.core?.headline?.text,
                this.data.core?.headline?.placeholders
            );
        }
    }

    @Input() openDropdownAsPopup = false;
    @Input() dialogPanelClass = '';
    @Input() useDifferForUpdate = false;

    @Output() primaryButtonClicked = new EventEmitter<MouseEvent>();
    @Output() secondaryButtonClicked = new EventEmitter<MouseEvent>();
    @Output() toggleClicked = new EventEmitter<boolean>();
    @Output() selectionChanged = new EventEmitter<NvListItem>();
    @Output() primaryContextButtonClicked = new EventEmitter<MouseEvent>();
    @Output() secondaryContextButtonClicked = new EventEmitter<MouseEvent>();

    leadElementType = NvHigListItemLeadingElementType;
    trailElementType = NvHigListItemTrailingElementType;

    spinnerDiameter = 16;

    private tvMode: boolean;

    constructor(
        @Optional() @Inject(NV_HIG_CONFIG) higConfig: NvHigConfig,
        private translate: TranslateService
    ) {
        this.tvMode = !!higConfig?.tvMode;
        if (this.tvMode) {
            this.spinnerDiameter *= TvImageSizeScale;
        }
    }

    primaryButtonClick(event: MouseEvent): void {
        this.primaryButtonClicked.emit(event);
    }

    secondaryButtonClick(event: MouseEvent): void {
        this.secondaryButtonClicked.emit(event);
    }

    toggleClick(checked: boolean): void {
        this.toggleClicked.emit(checked);
    }

    selectionChange(item: NvListItem): void {
        this.selectionChanged.emit(item);
    }

    primaryContextButtonClick(event: MouseEvent): void {
        this.primaryContextButtonClicked.emit(event);
    }

    secondaryContextButtonClick(event: MouseEvent): void {
        this.secondaryContextButtonClicked.emit(event);
    }

    getTranslatedAriaLabelForTrail(ariaLabel: string, placeholders?: object): string | null {
        if (!ariaLabel) {
            return null;
        }
        const translated = this.translate.instant(ariaLabel, placeholders);
        // Return the original key if translation fails
        return translated || ariaLabel;
    }
}
